import { useEffect } from 'react';

/**
 * Lightweight SEO hook — sets document title, meta description, Open Graph,
 * Twitter card and canonical URL for the current view. Restores defaults on unmount.
 */
const DEFAULT_TITLE = 'Hei Web Creations | Premium Web Design Singapore';
const DEFAULT_DESCRIPTION =
  'Premium websites built smarter with AI. Web design, WordPress, WooCommerce and portal development in Singapore.';
const DEFAULT_OG_IMAGE = '/og-image.png';
const SITE_URL =
  process.env.REACT_APP_SITE_URL ||
  (typeof window !== 'undefined' ? window.location.origin : '');

const GOOGLE_VERIFY = (process.env.REACT_APP_GOOGLE_SITE_VERIFICATION || '').trim();
const BING_VERIFY = (process.env.REACT_APP_BING_SITE_VERIFICATION || '').trim();

// One-time injection of search-engine verification meta tags (idempotent).
if (typeof document !== 'undefined') {
  const injectVerify = (name, content) => {
    if (!content) return;
    if (document.head.querySelector(`meta[name="${name}"]`)) return;
    const el = document.createElement('meta');
    el.setAttribute('name', name);
    el.setAttribute('content', content);
    document.head.appendChild(el);
  };
  injectVerify('google-site-verification', GOOGLE_VERIFY);
  injectVerify('msvalidate.01', BING_VERIFY);
}

const setMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setCanonical = (href) => {
  if (!href) return;
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
};

export const useSEO = ({ title, description, image, type = 'website', canonical } = {}) => {
  useEffect(() => {
    const finalTitle = title || DEFAULT_TITLE;
    const finalDescription = description || DEFAULT_DESCRIPTION;
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const finalCanonical = canonical || `${SITE_URL}${path}`;
    const finalImage = image
      ? (image.startsWith('http') ? image : `${SITE_URL}${image}`)
      : `${SITE_URL}${DEFAULT_OG_IMAGE}`;

    document.title = finalTitle;
    setMeta('name', 'description', finalDescription);
    setCanonical(finalCanonical);

    // Open Graph
    setMeta('property', 'og:title', finalTitle);
    setMeta('property', 'og:description', finalDescription);
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:url', finalCanonical);
    setMeta('property', 'og:site_name', 'Hei Web Creations');
    setMeta('property', 'og:image', finalImage);
    setMeta('property', 'og:image:width', '1200');
    setMeta('property', 'og:image:height', '630');

    // Twitter
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', finalTitle);
    setMeta('name', 'twitter:description', finalDescription);
    setMeta('name', 'twitter:image', finalImage);

    return () => {
      document.title = DEFAULT_TITLE;
      setMeta('name', 'description', DEFAULT_DESCRIPTION);
    };
  }, [title, description, image, type, canonical]);
};

export default useSEO;
